import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
    Search,
    Bell,
    User,
    Settings,
    HelpCircle,
    LogOut,
    ChevronDown,
    Building2,
    CreditCard,
    Shield,
} from 'lucide-react';
import styles from './Header.module.css';

export default function Header() {
    const { user, logout } = useAuth();
    const [profileOpen, setProfileOpen] = useState(false);
    const [notificationsOpen, setNotificationsOpen] = useState(false);
    const profileRef = useRef<HTMLDivElement>(null);
    const notificationRef = useRef<HTMLDivElement>(null);
    const navigate = useNavigate();

    const currentDate = new Date().toLocaleDateString('en-IN', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
    });

    // Close dropdowns when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (profileRef.current && !profileRef.current.contains(event.target as Node)) {
                setProfileOpen(false);
            }
            if (notificationRef.current && !notificationRef.current.contains(event.target as Node)) {
                setNotificationsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleLogout = () => {
        logout();
        navigate('/sign-in');
    };

    // Mock notifications
    const notifications = [
        { id: 1, type: 'alert', title: 'GSTR-3B Due', message: 'Filing deadline in 5 days', time: '2h ago' },
        { id: 2, type: 'success', title: 'Reconciliation Complete', message: '98% match rate achieved', time: '5h ago' },
        { id: 3, type: 'info', title: 'New Notice', message: 'You have a new GST notice', time: '1d ago' },
    ];

    // Derived User State
    const userName = user?.name || 'User';
    const userInitials = userName.charAt(0).toUpperCase();
    const userRole = user?.role || 'Member';
    const userEmail = user?.email || '';
    const orgName = user?.companyName || 'My Organization';

    return (
        <header className={styles.header}>
            <div className={styles.searchSection}>
                <div className={styles.searchBar}>
                    <Search size={18} />
                    <input
                        type="text"
                        placeholder="Search transactions, returns, notices..."
                    />
                </div>
            </div>

            <div className={styles.actionSection}>
                <span className={styles.date}>{currentDate}</span>

                {/* Notifications Dropdown */}
                <div className={styles.dropdownContainer} ref={notificationRef}>
                    <button
                        className={styles.notificationBtn}
                        onClick={() => setNotificationsOpen(!notificationsOpen)}
                    >
                        <Bell size={20} />
                        <span className={styles.notificationBadge}>3</span>
                    </button>

                    {notificationsOpen && (
                        <div className={styles.notificationDropdown}>
                            <div className={styles.dropdownHeader}>
                                <h4>Notifications</h4>
                                <button className={styles.markAllRead}>Mark all read</button>
                            </div>
                            <div className={styles.notificationList}>
                                {notifications.map((notif) => (
                                    <div key={notif.id} className={styles.notificationItem}>
                                        <div className={`${styles.notifIcon} ${styles[notif.type]}`}>
                                            <Bell size={14} />
                                        </div>
                                        <div className={styles.notifContent}>
                                            <span className={styles.notifTitle}>{notif.title}</span>
                                            <span className={styles.notifMessage}>{notif.message}</span>
                                        </div>
                                        <span className={styles.notifTime}>{notif.time}</span>
                                    </div>
                                ))}
                            </div>
                            <Link to="/app/notifications" className={styles.viewAll}>
                                View all notifications
                            </Link>
                        </div>
                    )}
                </div>

                {/* Profile Dropdown */}
                <div className={styles.dropdownContainer} ref={profileRef}>
                    <button
                        className={styles.profileBtn}
                        onClick={() => setProfileOpen(!profileOpen)}
                    >
                        <div className={styles.avatar}>{userInitials}</div>
                        <div className={styles.userInfo}>
                            <span className={styles.userName}>{userName}</span>
                            <span className={styles.userRole}>{userRole}</span>
                        </div>
                        <ChevronDown size={16} className={profileOpen ? styles.chevronUp : ''} />
                    </button>

                    {profileOpen && (
                        <div className={styles.profileDropdown}>
                            <div className={styles.profileHeader}>
                                <div className={styles.profileAvatarLarge}>{userInitials}</div>
                                <div className={styles.profileInfo}>
                                    <span className={styles.profileName}>{userName}</span>
                                    <span className={styles.profileEmail}>{userEmail}</span>
                                </div>
                            </div>

                            <div className={styles.profileOrg}>
                                <Building2 size={16} />
                                <div>
                                    <span className={styles.orgName}>{orgName}</span>
                                    {/* <span className={styles.orgGstin}>GSTIN: ...</span> */}
                                </div>
                            </div>

                            <div className={styles.dropdownDivider} />

                            <nav className={styles.profileMenu}>
                                <Link to="/app/profile" className={styles.menuItem} onClick={() => setProfileOpen(false)}>
                                    <User size={18} />
                                    <span>My Profile</span>
                                </Link>
                                <Link to="/app/settings" className={styles.menuItem} onClick={() => setProfileOpen(false)}>
                                    <Settings size={18} />
                                    <span>Settings</span>
                                </Link>
                                <Link to="/app/billing" className={styles.menuItem} onClick={() => setProfileOpen(false)}>
                                    <CreditCard size={18} />
                                    <span>Billing & Plan</span>
                                </Link>
                                <Link to="/app/security" className={styles.menuItem} onClick={() => setProfileOpen(false)}>
                                    <Shield size={18} />
                                    <span>Security</span>
                                </Link>
                                <a href="/help" className={styles.menuItem}>
                                    <HelpCircle size={18} />
                                    <span>Help & Support</span>
                                </a>
                            </nav>

                            <div className={styles.dropdownDivider} />

                            <button className={styles.logoutBtn} onClick={handleLogout}>
                                <LogOut size={18} />
                                <span>Sign out</span>
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </header>
    );
}
